import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  getClinicSettings,
  updateClinicSettings,
  setClinicName,
  lookupCnpj,
  ClinicSettings,
} from '@/services/clinic-settings';
import { isValidCnpj, normalizeCnpj } from '@valiosascrm/shared';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { applyClinicNameToSession, applyTenantSettingsToSession } from '@/lib/tenant-settings-sync';
import { useAuthStore } from '@/stores/auth';

const SETTINGS_KEY = 'clinic-settings';
export const clinicSettingsKey = [SETTINGS_KEY] as const;

// Story FE-Tenant: guard de hidratação (sem slug → sem request) + `enabled` opcional para o
// consumidor condicionar por permissão (GET /settings/clinic exige settings:view — professional
// abrindo a agenda gerava 403 garantido em prod).
export function useClinicSettings(options?: { enabled?: boolean }) {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: [SETTINGS_KEY],
    queryFn: getClinicSettings,
    enabled: !!tenantSlug && (options?.enabled ?? true),
  });
}

// Story 70.5 — efeito de cache da gravação, o mesmo na aba e na Configuração rápida (ADR 0002, D1.4).
export function afterClinicSettingsWrite(queryClient: QueryClient, data: ClinicSettings): void {
  queryClient.setQueryData([SETTINGS_KEY], data);
  // RC-065: o fuso da grade vem de `user.tenant.settings` (lido no login). Sem isto, a aba
  // que acabou de trocar o fuso continuava desenhando no fuso antigo até entrar de novo.
  // `clinicName` é `Tenant.name`, não chave de settings: vai para a sessão por applyClinicNameToSession.
  const settings: ClinicSettings = { ...data };
  delete settings.cancelledCampaigns;
  delete settings.clinicName;
  applyTenantSettingsToSession(settings as Record<string, unknown>);
}

// Story 61.4 AC6 / 67.6 AC 5 — o mesmo aviso no PUT da Clínica e no liga/pausa de Mensagens.
export function cancelledCampaignsMessage(cancelled: number): string {
  return cancelled === 1
    ? 'Automação desligada. 1 lote em andamento foi cancelado; as mensagens restantes não serão enviadas.'
    : `Automação desligada. ${cancelled} lotes em andamento foram cancelados; as mensagens restantes não serão enviadas.`;
}

// Story 67.8 — `notify: false` deixa a tela dar o aviso (a Clínica grava nome e página em duas
// partes e só diz "salvo" quando as duas gravaram).
export function useUpdateClinicSettings(options?: { notify?: boolean }) {
  const queryClient = useQueryClient();
  const notify = options?.notify ?? true;

  return useSameActionMutation({
    mutationFn: (data: ClinicSettings) => updateClinicSettings(data),
    onSuccess: (data) => {
      afterClinicSettingsWrite(queryClient, data);
      // Story 61.4 AC6 — desligar uma automação com lote em andamento cancela o lote (61.3 AC5).
      const cancelled = (data as { cancelledCampaigns?: number }).cancelledCampaigns ?? 0;
      if (!notify) return;
      if (cancelled > 0) {
        toast.success(cancelledCampaignsMessage(cancelled));
        return;
      }
      toast.success('Configurações salvas com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      if (!notify) return;
      toast.error(error.response?.data?.message || 'Erro ao salvar configurações');
    },
  });
}

/**
 * Story 67.8 — grava o nome da clínica em `Tenant.name` (o que mensagens e documentos leem) e
 * atualiza na hora a tela Clínica, o seletor de clínica, o Perfil e "Todas as clínicas". Sem
 * aviso próprio: quem chama diz o que foi salvo.
 */
export function useUpdateClinicName() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: (name: string) => setClinicName(name),
    onSuccess: (data) => afterClinicNameWrite(queryClient, data),
  });
}

// Story 70.10 — o efeito do nome gravado, o mesmo na tela Clínica e na Configuração rápida (ADR 0002, D1.4).
export function afterClinicNameWrite(queryClient: QueryClient, { name }: { name: string }): void {
  queryClient.setQueryData<ClinicSettings>([SETTINGS_KEY], (current) => (current ? { ...current, clinicName: name } : current));
  applyClinicNameToSession(useAuthStore.getState().user?.tenantId, name);
  queryClient.invalidateQueries({ queryKey: ['consolidated-overview'] });
}

/**
 * Story 50.2 — consulta o CNPJ na Receita, sem bloquear nada.
 *
 * AC6 (uma consulta por CNPJ, memorizada) sai de graça da `queryKey`: ela É o CNPJ
 * normalizado. Blur repetido no mesmo valor lê o cache; mudar o valor gera outra chave.
 * Não precisou de memo manual nem de ref — o React Query já faz isso, e uma implementação
 * própria seria uma engrenagem a mais para manter.
 *
 * AC1 — `enabled` só libera para CNPJ que passa no dígito verificador. O DV é local e de
 * graça; a chamada de rede não é.
 *
 * `retry: false` é decisão de produto, não descuido: a Receita fora do ar não melhora em
 * 300ms, e cada tentativa extra prolonga a espera de quem está com o cursor no campo
 * seguinte. O AC3 já garante que falhar não custa nada além do aviso.
 */
export function useCnpjLookup(cnpj: string) {
  const normalizado = normalizeCnpj(cnpj);
  return useQuery({
    queryKey: ['cnpj-lookup', normalizado],
    queryFn: () => lookupCnpj(normalizado),
    enabled: isValidCnpj(normalizado),
    retry: false,
    // O cadastro da Receita não muda durante um preenchimento de formulário.
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
}
